import {
  Box,
  Button,
  Collapse,
  CollapseHeader,
  CollapsePanel,
  Heading,
} from "@vinomofo/components";
import { PropsWithChildren, ReactNode, useState } from "react";

import { StaticImage } from "gatsby-plugin-image";
import { richTextRenderer } from "../../../utils/rich-text-renderer";
import styled from "styled-components";
import { trackEvent } from "../../../utils/track-event";

import { TextBlockProps } from "../../text-block/TextBlock";

const Container = styled(Box)`
  transition: color 0.3s ease-in-out;
`;

const Navigation = styled(Button)<{ active: boolean }>`
  color: ${({ active }) => (active ? "white" : "rgba(255, 255, 255, 0.3)")};
  border-color: ${({ active }) =>
    active ? "white" : "rgba(255, 255, 255, 0.3)"};
`;

const StyledCollapseHeader = styled(CollapseHeader)`
  &.whisky-faq-item-header {
    padding: 10px 0px;
  }

  div:has(> .faq-icon) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const StyledCollapsePanel = styled(CollapsePanel)`
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  strong {
    color: rgb(247, 248, 250);
  }
`;

export interface iFAQGroup {
  id: string;
  internalName: string;
  title: string;
  description: string;
  collections: Array<iFAQCollectionItem>;
}

export interface iFAQCollectionItem extends TextBlockProps {
  id: string;
  internalName: string;
  title: string;
  textBody: ReactNode;
}

interface AccordionProps {
  id: string;
  label: string;
  openIndex: string | null;
  setIndex: () => void;
}

const Accordion = ({
  id,
  label,
  children,
  openIndex,
  setIndex,
}: PropsWithChildren<AccordionProps>) => {
  return (
    <Collapse
      isOpen={openIndex === id}
      onClick={() => setIndex()}
      borderBottom="0.66px solid rgba(255, 255, 255, 0.3)"
    >
      <StyledCollapseHeader
        backgroundColor="transparent"
        py="20px"
        px="0"
        iconSize={["35px", "45px", "55px"]}
        icon={
          <StaticImage
            src="./whisky-faq-arrow.svg"
            objectFit="contain"
            alt="faq-icon"
            className="faq-icon"
          />
        }
        closeIcon={
          <StaticImage
            src="./whisky-faq-arrow.svg"
            objectFit="contain"
            alt="faq-icon"
            className="faq-icon"
            style={{
              rotate: "180deg",
            }}
          />
        }
        className="whisky-faq-item-header"
      >
        <Box mr="20px">
          <Heading
            as="h2"
            variant="xl"
            textAlign="left"
            color="almostWhite"
            fontSize={["16px", "26px"]}
            fontFamily="heading"
          >
            {label}
          </Heading>
        </Box>
      </StyledCollapseHeader>
      <StyledCollapsePanel
        backgroundColor="transparent"
        padding="0px 0 15px"
        color="white"
      >
        {children}
      </StyledCollapsePanel>
    </Collapse>
  );
};

export const FAQCollections = ({
  faqgroup,
}: {
  faqgroup: Array<iFAQGroup>;
}): JSX.Element => {
  if (faqgroup.length === 0) return <></>;
  const [activeCategory, setActiveCategory] = useState(faqgroup[0]);
  const [openIndex, setOpenIndex] = useState<string | null>(null);

  return (
    <Container
      display="flex"
      flexDirection={["column", "unset", "unset"]}
      gridGap={["0", "30px", "50px"]}
      width="100%"
      mt={["-25px", "-25px", "-25px"]}
      color="white"
      pb="36px"
      fontFamily="heading"
      position="relative"
    >
      <Box
        display="flex"
        flexDirection="column"
        gridGap={["15px 15px", "15px"]}
        width={["100%", "30%", "30%"]}
        mt={["25px", "25px", "0"]}
      >
        {faqgroup.map((item, i) => {
          return (
            <Navigation
              active={item.id === activeCategory.id}
              key={i + item.id}
              hoverColor="white"
              color="rgba(255, 255, 255, 0.3)"
              borderBottom="1px solid rgba(255, 255, 255, 0.3)"
              onClick={() => {
                trackEvent("FAQ Category Selected", {
                  id: item.id,
                  title: item.title,
                  list: item.collections,
                });
                setActiveCategory(item);
                setOpenIndex(null);
              }}
              textTransform="uppercase"
              textAlign="left"
              fontFamily="heading"
              fontSize="3"
              p="15px 5px"
            >
              {item.title}
            </Navigation>
          );
        })}
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        gridGap="0"
        width={["100%", "70%", "70%"]}
        mt={["25px", "25px", "0"]}
      >
        {!activeCategory.collections ? (
          <p>No FAQs available for this category.</p>
        ) : (
          activeCategory.collections.map((item, index) => {
            const { bodyText } = item;

            return (
              <Accordion
                key={index + item.id}
                label={item.title || item.internalName}
                id={item.id}
                setIndex={() => {
                  if (openIndex !== item.id)
                    trackEvent("FAQ Item Viewed", {
                      id: item.id,
                      title: item.title || item.internalName,
                      textBody: bodyText,
                      category: activeCategory.title,
                    });
                  setOpenIndex(openIndex === item.id ? null : item.id);
                }}
                openIndex={openIndex}
              >
                {bodyText &&
                  bodyText?.json &&
                  richTextRenderer(bodyText, {
                    p: {
                      fontSize: [16, 17],
                      color: "white",
                      fontFamily: "heading",
                    },
                  })}
              </Accordion>
            );
          })
        )}
      </Box>
    </Container>
  );
};